import { useCallback, useEffect, useState } from 'react';
import { Laptop, Smartphone, Globe, Copy, Check, Fingerprint, Trash2 } from 'lucide-react';
import { useStore } from '../../store/StoreContext';
import {
  fetchSecurity,
  setTwoFactor,
  fetchAuthenticatorStatus,
  beginAuthenticatorEnroll,
  confirmAuthenticatorEnroll,
  cancelAuthenticatorEnroll,
  disableAuthenticator,
  regenerateAuthenticatorBackupCodes,
  type AuthenticatorStatus,
  type AuthenticatorEnrollment,
} from '../../api/authApi';
import {
  defaultPasskeyLabel,
  fetchPasskeyAvailable,
  listPasskeys,
  registerPasskey,
  removePasskey,
  type PasskeyInfo,
} from '../../api/passkeyApi';
import { fetchSessions, revokeSession, revokeOtherSessions, type ActiveSession } from '../../api/dataApi';
import { ScreenHeader } from '../../components/ui/ScreenHeader';
import { FormSection } from '../../components/ui/FormSection';
import { TextField } from '../../components/ui/TextField';
import { Button } from '../../components/ui/Button';
import { Toggle } from '../../components/ui/Toggle';
import { Toast } from '../../components/ui/Toast';
import { AutoSaveStatus } from '../../components/ui/AutoSaveStatus';
import { useFeature } from '../../lib/features';
import { FeatureGate } from '../../components/FeatureGate';
import { useAutoSave } from '../../lib/useAutoSave';
import { uid } from '../../types';

function relativeTime(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const secs = Math.round((Date.now() - t) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

// Passkeys: phishing-resistant sign-in with Touch ID / Face ID / Windows
// Hello / a security key. Hidden entirely when the server has passkeys off
// or this browser can't do WebAuthn. Additive - the password keeps working.
function PasskeysSection() {
  const flagOn = useFeature('auth.passkeys');
  const [available, setAvailable] = useState(false);
  const [passkeys, setPasskeys] = useState<PasskeyInfo[] | null>(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const load = useCallback(() => {
    listPasskeys().then(setPasskeys);
  }, []);

  useEffect(() => {
    fetchPasskeyAvailable().then((ok) => {
      setAvailable(ok);
      if (ok) load();
    });
  }, [load]);

  const add = async () => {
    setBusy('add');
    setError('');
    const result = await registerPasskey(defaultPasskeyLabel());
    setBusy('');
    if (result.ok) {
      setToast('Passkey added.');
      load();
    } else if (!result.cancelled) {
      setError(result.error);
    }
  };

  const remove = async (id: string) => {
    setBusy(id);
    setError('');
    const result = await removePasskey(id);
    setBusy('');
    if (result.ok) load();
    else setError(result.error);
  };

  if (!available || !flagOn) return null;

  return (
    <FormSection title="Passkeys">
      <p className="text-xs text-black/50 dark:text-white/50">
        Sign in with your fingerprint, face, or a security key - no password to type or phish.
      </p>
      {error && <p className="text-xs text-red-500">{error}</p>}
      {passkeys && passkeys.length > 0 && (
        <ul className="flex flex-col divide-y divide-black/5 dark:divide-white/10">
          {passkeys.map((p) => (
            <li key={p.id} className="flex items-center gap-3 py-2.5">
              <Fingerprint size={18} className="shrink-0 text-black/50 dark:text-white/50" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{p.label}</div>
                <div className="text-xs text-black/50 dark:text-white/50">
                  Added {relativeTime(p.createdAt)}
                  {p.lastUsedAt && !p.lastUsedAt.startsWith('0001') ? ` - used ${relativeTime(p.lastUsedAt)}` : ' - never used'}
                  {p.synced ? ' - synced' : ''}
                </div>
              </div>
              <button
                type="button"
                aria-label={`Remove passkey ${p.label}`}
                disabled={busy !== ''}
                onClick={() => remove(p.id)}
                className="rounded-lg p-2 text-red-500 hover:bg-red-500/10 disabled:opacity-50"
              >
                <Trash2 size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <Button variant="bordered" full loading={busy === 'add'} onClick={add} disabled={busy !== ''}>
        Add a passkey
      </Button>
      {toast && <Toast message={toast} onDismiss={() => setToast('')} />}
    </FormSection>
  );
}

function SessionsSection() {
  const [sessions, setSessions] = useState<ActiveSession[] | null>(null);
  const [busy, setBusy] = useState<string>('');
  const [error, setError] = useState('');

  const load = useCallback(() => {
    fetchSessions()
      .then(setSessions)
      .catch(() => setError('Could not load your sessions.'));
  }, []);

  useEffect(load, [load]);

  const revoke = async (id: string) => {
    setBusy(id);
    setError('');
    try {
      await revokeSession(id);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not revoke that session.');
    } finally {
      setBusy('');
    }
  };

  const revokeOthers = async () => {
    setBusy('others');
    setError('');
    try {
      await revokeOtherSessions();
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not sign out other sessions.');
    } finally {
      setBusy('');
    }
  };

  const hasOthers = (sessions ?? []).some((s) => !s.current);

  return (
    <FormSection title="Active sessions">
      {error && <p className="text-xs text-red-500">{error}</p>}
      {sessions === null ? (
        <p className="text-xs text-black/50 dark:text-white/50">Loading…</p>
      ) : sessions.length === 0 ? (
        <p className="text-xs text-black/50 dark:text-white/50">No other sessions.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-black/5 dark:divide-white/10">
          {sessions.map((s) => {
            const Icon = s.platform === 'ios' || s.platform === 'android' ? Smartphone : s.platform === 'web' ? Laptop : Globe;
            return (
              <li key={s.id} className="flex items-center gap-3 py-2.5">
                <Icon className="size-4 shrink-0 text-black/40 dark:text-white/40" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">
                    {s.platform || 'unknown'}
                    {s.current && <span className="ml-2 text-xs text-green-600 dark:text-green-400">this device</span>}
                  </p>
                  <p className="truncate text-xs text-black/50 dark:text-white/50">
                    {s.ip || 'unknown IP'} · active {relativeTime(s.lastSeenAt || s.issuedAt) || '—'}
                  </p>
                </div>
                {!s.current && (
                  <button
                    onClick={() => revoke(s.id)}
                    disabled={busy === s.id}
                    className="shrink-0 text-xs text-red-500 disabled:opacity-40"
                  >
                    {busy === s.id ? '…' : 'Revoke'}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {hasOthers && (
        <Button variant="bordered" full disabled={busy === 'others'} onClick={revokeOthers}>
          Sign out all other sessions
        </Button>
      )}
    </FormSection>
  );
}

// One-time backup-code reveal, shared by the confirm-enrollment and
// regenerate flows below - both hand back a fresh set of plaintext codes
// that only ever exist client-side for this one render (the server only
// ever stores their bcrypt hashes - see github.com/kuetix/authenticator's
// store.go doc comment).
function BackupCodesReveal({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const [copied, setCopied] = useState(false);

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      setCopied(true);
    } catch {
      // clipboard access denied - the codes are still selectable text below
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
      <p className="text-sm font-medium">Save your backup codes</p>
      <p className="text-xs text-black/60 dark:text-white/60">
        Each code works once, if you lose access to your authenticator app. This is the only time they're shown.
      </p>
      <div className="grid grid-cols-2 gap-1.5 rounded-md bg-black/5 p-3 font-mono text-sm dark:bg-white/10">
        {codes.map((c) => (
          <span key={c} className="select-all">
            {c}
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Button variant="bordered" onClick={copyAll}>
          {copied ? 'Copied' : 'Copy all'}
        </Button>
        <Button variant="primary" onClick={onDone}>
          Done
        </Button>
      </div>
    </div>
  );
}

function AuthenticatorSection() {
  const [status, setStatus] = useState<AuthenticatorStatus | null>(null);
  const [enrollment, setEnrollment] = useState<AuthenticatorEnrollment | null>(null);
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const loadStatus = useCallback(() => {
    fetchAuthenticatorStatus().then(setStatus);
  }, []);

  useEffect(loadStatus, [loadStatus]);

  const begin = async () => {
    setBusy(true);
    setError('');
    try {
      setEnrollment(await beginAuthenticatorEnroll());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start authenticator setup.');
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (!code.trim()) return setError('Enter the code from your authenticator app.');
    setBusy(true);
    setError('');
    try {
      const result = await confirmAuthenticatorEnroll(code.trim());
      if (result.ok && result.backupCodes) {
        setEnrollment(null);
        setCode('');
        setBackupCodes(result.backupCodes);
        loadStatus();
      } else {
        setError(result.error ?? "That code didn't match - check the time on your device and try again.");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not confirm the code.');
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    setEnrollment(null);
    setCode('');
    setError('');
    await cancelAuthenticatorEnroll();
  };

  const disable = async () => {
    setBusy(true);
    try {
      const result = await disableAuthenticator();
      if (result.ok) {
        setToast('Authenticator app disabled.');
        loadStatus();
      } else {
        setToast(result.error ?? 'Could not disable the authenticator.');
      }
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async () => {
    setBusy(true);
    try {
      const result = await regenerateAuthenticatorBackupCodes();
      if (result.ok && result.backupCodes) {
        setBackupCodes(result.backupCodes);
      } else {
        setToast(result.error ?? 'Could not regenerate backup codes.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <FormSection title="Authenticator app">
      {backupCodes ? (
        <BackupCodesReveal codes={backupCodes} onDone={() => setBackupCodes(null)} />
      ) : enrollment ? (
        <div className="flex flex-col gap-3">
          <p className="text-xs text-black/60 dark:text-white/60">
            Scan this with Google Authenticator, Authy, 1Password, or any TOTP app - or enter the code manually.
          </p>
          <img
            src={enrollment.qrCodeDataUrl}
            alt="Authenticator setup QR code"
            className="h-44 w-44 self-center rounded-md border border-black/10 bg-white p-2 dark:border-white/10"
          />
          <p className="break-all rounded-md bg-black/5 p-2 text-center font-mono text-xs dark:bg-white/10">{enrollment.secret}</p>
          <TextField
            label="6-digit code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          {error && <p className="text-xs text-red-500">{error}</p>}
          <div className="flex gap-2">
            <Button variant="bordered" onClick={cancel} disabled={busy}>
              Cancel
            </Button>
            <Button variant="primary" full onClick={confirm} disabled={busy}>
              Confirm
            </Button>
          </div>
        </div>
      ) : status?.enabled ? (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-black/70 dark:text-white/70">
            Enabled - {status.backupCodesRemaining ?? 0} backup code{status.backupCodesRemaining === 1 ? '' : 's'} remaining.
          </p>
          <div className="flex gap-2">
            <Button variant="bordered" onClick={regenerate} disabled={busy}>
              New backup codes
            </Button>
            <Button variant="bordered" onClick={disable} disabled={busy}>
              Disable
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <p className="text-xs text-black/50 dark:text-white/50">
            Use an app like Google Authenticator or 1Password to generate sign-in codes instead of email.
          </p>
          <Button variant="bordered" full onClick={begin} disabled={busy || status === null}>
            Set up authenticator app
          </Button>
        </div>
      )}
      {toast && <Toast message={toast} onDismiss={() => setToast('')} />}
    </FormSection>
  );
}

// The account's Cue key/secret - masked by default (TextField's own
// type="password" eye toggle reveals it) so they aren't sitting on screen in
// plaintext for anyone looking over a shoulder or at a screen share, with a
// Copy button so using them never requires revealing them.
// autoComplete="off" keeps password managers from offering to save them as
// a login password.
function CredentialField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(t);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // clipboard access denied - the eye toggle still reveals the value
    }
  };

  return (
    <div className="flex items-end gap-2">
      <TextField
        label={label}
        type="password"
        autoComplete="off"
        spellCheck={false}
        className="font-mono"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <Button variant="bordered" className="shrink-0" onClick={copy} disabled={!value} aria-label={`Copy ${label.toLowerCase()}`}>
        {copied ? <Check size={16} /> : <Copy size={16} />}
        {copied ? 'Copied' : 'Copy'}
      </Button>
    </div>
  );
}

export function SecurityScreen() {
  const { security, setSecurity } = useStore();
  const [draft, setDraft] = useState(security);
  const auto = useAutoSave(draft, setSecurity);

  const [twoFactor, setTwoFactorState] = useState<boolean | null>(null);
  const [twoFactorBusy, setTwoFactorBusy] = useState(false);
  const [toast, setToast] = useState('');

  // GET /security now also reports the account's 2FA state - fetch it on
  // mount so the toggle shows the real value rather than assuming off.
  useEffect(() => {
    let cancelled = false;
    fetchSecurity().then((remote) => {
      if (!cancelled && remote) setTwoFactorState(remote.twofactor);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleTwoFactor = async (next: boolean) => {
    setTwoFactorBusy(true);
    const result = await setTwoFactor(next);
    setTwoFactorBusy(false);
    if (result.success) {
      setTwoFactorState(next);
      setToast(result.message ?? (next ? 'Two-factor authentication enabled.' : 'Two-factor authentication disabled.'));
    } else {
      setToast(result.error ?? 'Could not update two-factor authentication.');
    }
  };

  return (
    <div className="flex flex-col gap-5" onBlur={auto.flush}>
      <ScreenHeader title="Security" action={<AutoSaveStatus status={auto.status} onRetry={auto.retry} />} />

      <FeatureGate feature="auth.email_2fa" fallback={null}>
      <FormSection title="Two-factor authentication">
        <Toggle
          label="Email a sign-in code on every login"
          checked={twoFactor === true}
          disabled={twoFactor === null || twoFactorBusy}
          onChange={toggleTwoFactor}
        />
        <p className="text-xs text-black/50 dark:text-white/50">
          When on, logging in also requires a one-time code sent to your email address.
        </p>
      </FormSection>
      </FeatureGate>

      <PasskeysSection />

      <FeatureGate feature="auth.authenticator_app" fallback={null}>
        <AuthenticatorSection />
      </FeatureGate>

      <FeatureGate feature="account.sessions" fallback={null}>
        <SessionsSection />
      </FeatureGate>

      <FormSection title="Credentials">
        <CredentialField label="Key" value={draft.key} onChange={(key) => setDraft({ ...draft, key })} />
        <CredentialField label="Secret" value={draft.secret} onChange={(secret) => setDraft({ ...draft, secret })} />
      </FormSection>

      <Button
        variant="bordered"
        full
        onClick={() => setDraft({ ...draft, secret: uid().replace(/-/g, '') })}
      >
        Regenerate secret
      </Button>

      {toast && <Toast message={toast} onDismiss={() => setToast('')} />}
    </div>
  );
}
